import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// SECURITY: Token disimpan di httpOnly cookie (di-set oleh backend via Set-Cookie
// pada /api/auth/login & /api/auth/register). httpOnly = JS tidak bisa baca token,
// jadi kebal terhadap pencurian via XSS. `withCredentials: true` di axios memastikan
// cookies otomatis dikirim pada setiap request.
//
// Backend (auth_utils.py) membaca cookie `access_token` jika header Authorization
// kosong. Tidak ada lagi penyimpanan token di localStorage/sessionStorage.

const isDev = process.env.NODE_ENV === "development";

// Legacy no-op stubs (backwards compat — komponen lama yang mungkin masih import
// fungsi-fungsi ini tidak akan error). Token TIDAK lagi disimpan di JS storage.
export function getToken() { return null; }
export function setToken(_token) { /* noop — handled by httpOnly cookie */ }
export function clearToken() { /* noop — backend clear via /api/auth/logout */ }

const api = axios.create({
  baseURL: API,
  withCredentials: true, // WAJIB: kirim httpOnly cookie auth otomatis
  headers: { "Content-Type": "application/json" },
});

// P0 Multi-Tenant Isolation — dev tenant simulation (preview/localhost ONLY).
// Bila HostContext dev override aktif (localStorage `dynicty_dev_tenant`),
// kirim X-Tenant-Host supaya backend resolve tenant yang disimulasikan.
// Di production, host asli (*.dynicty.com) yang dipakai backend.
const DEV_HOST_SUFFIXES = [".preview.emergentagent.com", ".emergentagent.com", "localhost", "127.0.0.1"];
function devTenantHost() {
  try {
    const h = window.location.hostname.toLowerCase();
    const isDevHost = DEV_HOST_SUFFIXES.some((s) => h === s.replace(/^\./, "") || h.endsWith(s));
    if (!isDevHost) return null;
    const t = window.localStorage.getItem("dynicty_dev_tenant");
    return t ? `${t}.dynicty.com` : null;
  } catch {
    return null;
  }
}
api.interceptors.request.use((config) => {
  const th = devTenantHost();
  if (th) config.headers["X-Tenant-Host"] = th;
  return config;
});

// Global 401 handler: bersihkan auth state + dispatch event agar AuthContext
// langsung tahu session sudah expired. AdminLayout akan auto-render layar
// "Akses Ditolak" (link ke /login) — TIDAK muncul toast merah.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = err?.config?.url || "";
    if (
      err?.response?.status === 401 &&
      !url.includes("/auth/login") &&
      !url.includes("/auth/me")
    ) {
      // Defense in depth: purge any legacy localStorage token from older builds
      try { localStorage.removeItem("st_access_token"); } catch (_) { /* ignore */ }
      // Notify AuthContext (one-shot per tick) supaya UI berpindah ke layar login.
      try {
        if (typeof window !== "undefined" && !window.__authExpiredFired) {
          window.__authExpiredFired = true;
          window.dispatchEvent(new CustomEvent("auth:expired"));
          // reset flag agar event berikutnya tetap bisa dispatch
          setTimeout(() => { window.__authExpiredFired = false; }, 1000);
        }
      } catch (_) { /* ignore */ }
      // Tandai error agar caller bisa skip toast
      err.__isAuth401 = true;
    }
    return Promise.reject(err);
  }
);

// Helper: cek apakah error berasal dari 401 yang sudah ditangani interceptor.
// Caller pakai ini supaya tidak menampilkan toast merah "Not authenticated".
export function isAuthError(err) {
  return err?.__isAuth401 === true || err?.response?.status === 401;
}

// Helper terpadu untuk menampilkan error API. Auto-skip toast pada 401
// (sudah ditangani global interceptor → redirect ke layar login).
// Pakai ini sebagai pengganti `toast.error(formatApiError(e))` di semua
// halaman supaya error 401 tidak menimbulkan alert merah besar.
export function notifyError(err, fallback) {
  if (isAuthError(err)) return;
  toast.error(formatApiError(err) || fallback || "Terjadi kesalahan");
}

export default api;

export function formatRp(n) {
  if (n === null || n === undefined) return "Rp 0";
  return "Rp " + Number(n).toLocaleString("id-ID");
}

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || "Terjadi kesalahan";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(", ");
  if (typeof d === "object" && d.msg) return d.msg;
  return String(d);
}

export function imageUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://")) return pathOrUrl;
  if (pathOrUrl.startsWith("/api/")) return `${BACKEND_URL}${pathOrUrl}`;
  // /uploads/... adalah URL statik Nginx yang dikembalikan oleh backend
  // store_upload (r.data.url). Prefix dengan BACKEND_URL supaya cross-origin
  // preview tetap valid (mencegah double-slash saat dibungkus /api/files/).
  if (pathOrUrl.startsWith("/uploads/")) return `${BACKEND_URL}${pathOrUrl}`;
  // Absolute path lain (mis. /brand/... aset statik frontend) — pakai apa adanya.
  if (pathOrUrl.startsWith("/")) return pathOrUrl;
  return `${API}/files/${pathOrUrl}`;
}

// Dev-only logger — production tidak boleh leak debug info
export function devLog(...args) {
  if (isDev) console.log(...args);
}
export function devWarn(...args) {
  if (isDev) console.warn(...args);
}
export function devError(...args) {
  // error tetap dilog di prod (untuk Sentry/error tracker), tapi tanpa info sensitif
  if (isDev) console.error(...args);
}
